import { z } from 'zod';
import { dayModeOf, isoDateSchema, type DayMode } from './day';
import { layoutConfigSchema } from './layout';
import { mapZoneSchema, type MapResponse, type MapSpot, type MapVenue } from './map';
import { MINUTES_PER_DAY, holdConfigSchema } from './pricing';
import { paymentModeSchema } from './reservation';
import { cancellationPolicySchema } from './settings';
import type { SpotStatus } from './spot-status';

// What decides availability: the flag, the bookings, and today's own tile. §12
export type AvailabilityInput = {
  readonly staffOnly: boolean;
  readonly status: SpotStatus | null;
  readonly held: boolean;
};

// The one availability rule. A spot kept for staff and a spot already taken
// answer the same way, and nothing here says which it was. §11, §12
export function spotAvailable(spot: AvailabilityInput, mode: DayMode): boolean {
  if (spot.staffOnly || spot.held) return false;
  return mode === 'live' ? spot.status === 'free' : mode === 'future';
}

// Six fields, one of them state: no name, no status, no history, ever. §12
export const publicSpotSchema = z.object({
  id: z.uuid(),
  number: z.number().int().positive(),
  gridRow: z.number().int().min(1),
  gridCol: z.number().int().min(1),
  zoneKey: z.string(),
  available: z.boolean(),
});

export type PublicSpot = z.infer<typeof publicSpotSchema>;

// The venue as a visitor may know it: how it books, prices and refunds. §12, §13
export const publicVenueSchema = z.object({
  slug: z.string(),
  name: z.string(),
  spotKind: z.string(),
  currency: z.string(),
  timezone: z.string(),
  languages: z.array(z.string()).min(1),
  defaultLanguage: z.string(),
  hold: holdConfigSchema,
  paymentMode: paymentModeSchema,
  cancellation: cancellationPolicySchema,
  layout: layoutConfigSchema,
  zones: z.array(mapZoneSchema),
});

export type PublicVenue = z.infer<typeof publicVenueSchema>;

// nowMinutes is the venue's own clock, so the hold dropdown reads the same. §9
export const availabilityResponseSchema = z.object({
  venue: publicVenueSchema,
  date: isoDateSchema,
  today: isoDateSchema,
  nowMinutes: z.number().int().min(0).max(MINUTES_PER_DAY),
  spots: z.array(publicSpotSchema),
});

export type AvailabilityResponse = z.infer<typeof availabilityResponseSchema>;

export const availabilityQuerySchema = z.object({ date: isoDateSchema.optional() });

// Forward only, today the earliest: a visitor never looks back. §12
export function visitorDate(asked: string | undefined, today: string): string {
  return asked === undefined || asked < today ? today : asked;
}

// The one renderer's floor: available reads free, unavailable reserved. §7
export function visitorSpots(spots: readonly PublicSpot[]): MapSpot[] {
  return spots.map((spot) => ({
    id: spot.id,
    number: spot.number,
    gridRow: spot.gridRow,
    gridCol: spot.gridCol,
    zoneKey: spot.zoneKey,
    staffOnly: false,
    pendingCheckout: false,
    status: spot.available ? 'free' : 'reserved',
    occupations: 0,
    reservations: [],
  }));
}

// The venue the map renderer wants, out of the one the visitor was answered.
export function visitorVenue(answer: AvailabilityResponse): MapVenue {
  return {
    venueId: VISITOR_VENUE_ID,
    name: answer.venue.name,
    spotKind: answer.venue.spotKind,
    currency: answer.venue.currency,
    timezone: answer.venue.timezone,
    hold: answer.venue.hold,
    layout: answer.venue.layout,
    zones: answer.venue.zones,
  };
}

// A visitor is never told a venue's id; the renderer only needs a stable one.
export const VISITOR_VENUE_ID = '00000000-0000-4000-8000-000000000000';

export function visitorMap(answer: AvailabilityResponse): MapResponse {
  return {
    venue: visitorVenue(answer),
    date: answer.date,
    today: answer.today,
    nowMinutes: answer.nowMinutes,
    mode: dayModeOf(answer.date, answer.today),
    spots: visitorSpots(answer.spots),
  };
}

// An edge may hold it briefly; a browser has a nudge instead. §12, §16
export const AVAILABILITY_CACHE_CONTROL =
  'public, max-age=0, s-maxage=15, stale-while-revalidate=45';

// A nudge with no payload: the room re-reads, and nothing can leak. §12, §16
export const AVAILABILITY_CHANGE_EVENT = 'availability.change';

export const JOIN_PUBLIC_EVENT = 'public.join';

export const joinPublicSchema = z.object({ slug: z.string().min(1).max(120) });

export function publicRoom(venueId: string): string {
  return `public:${venueId}`;
}
